import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, Trophy } from 'lucide-react';
import axios from 'axios';

const Fixtures = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchFixtures();
  }, []);

  const fetchFixtures = async () => {
    try {
      const response = await axios.get('/fixtures');
      setFixtures(response.data);
    } catch (error) {
      console.error('Error fetching fixtures:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFixtures = fixtures.filter(fixture => {
    if (filter === 'all') return true;
    return fixture.status === filter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading fixtures...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Fixtures & Results</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Keep up with all Crawley Athletic matches, past and future
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="mb-8 flex justify-center">
          <div className="flex space-x-2 bg-white rounded-lg p-2 shadow">
            {[
              { key: 'all', label: 'All Matches' },
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'completed', label: 'Results' }
            ].map((filterOption) => (
              <button
                key={filterOption.key}
                onClick={() => setFilter(filterOption.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === filterOption.key
                    ? 'bg-black text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filterOption.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fixtures List */}
        {filteredFixtures.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No fixtures found</h3>
            <p className="text-gray-600">
              {fixtures.length === 0 
                ? "The fixture list will be updated soon."
                : "No fixtures match the selected filter."
              }
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredFixtures.map((fixture) => (
              <FixtureCard key={fixture.id} fixture={fixture} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Separate component for expandable fixture cards
const FixtureCard = ({ fixture }) => {
  const [expanded, setExpanded] = useState(false);
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`fixture-card cursor-pointer transition-all duration-200 ${
        expanded ? 'ring-2 ring-black ring-opacity-20' : 'hover:shadow-lg'
      }`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        {/* Date & Time */}
        <div className="mb-4 md:mb-0">
          <div className="fixture-date inline-block mb-2">
            {formatDate(fixture.match_date)}
          </div>
          <div className="flex items-center text-gray-600">
            <Clock className="h-4 w-4 mr-1" />
            {fixture.match_time}
          </div>
        </div>

        {/* Match Details */}
        <div className="flex-1 md:px-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-2">
              <div className="text-right">
                <h3 className="font-semibold text-lg">
                  {fixture.is_home ? 'Crawley Athletic' : fixture.away_team}
                </h3>
                {fixture.status === 'completed' && (
                  <span className="text-2xl font-bold">
                    {fixture.is_home ? fixture.home_score : fixture.away_score}
                  </span>
                )}
              </div>
              
              <div className="px-4">
                <span className="text-gray-400 font-medium">
                  {fixture.status === 'completed' ? '-' : 'vs'}
                </span>
              </div>
              
              <div className="text-left">
                <h3 className="font-semibold text-lg">
                  {fixture.is_home ? fixture.away_team : 'Crawley Athletic'}
                </h3>
                {fixture.status === 'completed' && (
                  <span className="text-2xl font-bold">
                    {fixture.is_home ? fixture.away_score : fixture.home_score}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-center text-sm text-gray-500">
              <MapPin className="h-4 w-4 mr-1" />
              {fixture.venue}
            </div>
            
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                <Trophy className="h-3 w-3 mr-1" />
                {fixture.competition}
              </span>
            </div>
          </div>
        </div>

        {/* Status & Expand Indicator */}
        <div className="mt-4 md:mt-0 flex items-center space-x-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            fixture.status === 'upcoming' 
              ? 'bg-blue-100 text-blue-800'
              : fixture.status === 'completed'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {fixture.status === 'upcoming' ? 'Upcoming' : 
             fixture.status === 'completed' ? 'Full Time' : 
             'Cancelled'}
          </span>
          
          {/* Expand indicator */}
          <div className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Date:</span>
              <span className="ml-2 text-gray-600">{formatDate(fixture.match_date)}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Kick-off:</span>
              <span className="ml-2 text-gray-600">{fixture.match_time}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Venue:</span>
              <span className="ml-2 text-gray-600">{fixture.venue}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Competition:</span>
              <span className="ml-2 text-gray-600">{fixture.competition}</span>
            </div>
            {fixture.status === 'completed' && (
              <>
                <div>
                  <span className="font-medium text-gray-700">Final Score:</span>
                  <span className="ml-2 text-gray-600 font-semibold">
                    {fixture.is_home ? `${fixture.home_score} - ${fixture.away_score}` : `${fixture.away_score} - ${fixture.home_score}`}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Result:</span>
                  <span className="ml-2">
                    {(() => {
                      const homeScore = fixture.home_score;
                      const awayScore = fixture.away_score;
                      const crawleyScore = fixture.is_home ? homeScore : awayScore;
                      const opponentScore = fixture.is_home ? awayScore : homeScore;
                      
                      if (crawleyScore > opponentScore) {
                        return <span className="text-green-600 font-semibold">Win</span>;
                      } else if (crawleyScore < opponentScore) {
                        return <span className="text-red-600 font-semibold">Loss</span>;
                      } else {
                        return <span className="text-yellow-600 font-semibold">Draw</span>;
                      }
                    })()}
                  </span>
                </div>
              </>
            )}
          </div>
          
          {/* Match Report */}
          {fixture.match_report && (
            <MatchReport report={fixture.match_report} />
          )}
          
          <div className="pt-2 text-center">
            <span className="text-xs text-gray-500">Click to collapse details</span>
          </div>
        </div>
      )}
      
      {/* Hint for non-expanded cards */}
      {!expanded && (
        <div className="mt-2 text-center">
          <span className="text-xs text-gray-400">Click to view more details</span>
        </div>
      )}
    </div>
  );
};

// Separate component for expandable match reports
const MatchReport = ({ report }) => {
  const [expanded, setExpanded] = useState(false);
  const isLongReport = report.length > 200;
  
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold text-gray-900 mb-2">Match Report</h4>
      <div className="text-gray-600 text-sm">
        {expanded || !isLongReport ? (
          <div>
            <p className="whitespace-pre-line">{report}</p>
            {isLongReport && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(false);
                }}
                className="mt-2 text-black font-semibold hover:underline transition-colors"
              >
                Show Less
              </button>
            )}
          </div>
        ) : (
          <div>
            <p className="line-clamp-3">{report}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(true);
              }}
              className="mt-2 text-black font-semibold hover:underline transition-colors"
            >
              Read More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fixtures;